import { Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import type {
  LedgerFilters as LedgerFiltersType,
  ChartOfAccounts,
} from '../../types/drawdown';
import { DEFAULT_LEDGER_VIEW } from '../../types/drawdown';
import { LedgerViewSelect } from './LedgerViewSelect';

interface LedgerFiltersProps {
  filters: LedgerFiltersType;
  fundId?: string;
  isLoadingChartOfAccounts: boolean;
  chartOfAccounts: ChartOfAccounts | null | undefined;
  onFilterChange: (
    key: keyof LedgerFiltersType,
    value: string | undefined
  ) => void;
  onClearFilters: () => void;
}

export function LedgerFilters({
  filters,
  fundId,
  isLoadingChartOfAccounts,
  chartOfAccounts,
  onFilterChange,
  onClearFilters,
}: LedgerFiltersProps) {
  const hasActiveFilters =
    filters.reference ||
    filters.counterparty ||
    filters.fund_id ||
    filters.account_code ||
    filters.reference_type ||
    filters.date_from ||
    filters.date_to ||
    (filters.view && filters.view !== DEFAULT_LEDGER_VIEW);

  return (
    <div className="flex items-center gap-2">
      {!fundId && (
        <Select
          value={filters.fund_id || 'all'}
          onValueChange={(value) =>
            onFilterChange('fund_id', value === 'all' ? undefined : value)
          }
        >
          <SelectTrigger className="w-28 h-8">
            <SelectValue placeholder="Fund" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Funds</SelectItem>
          </SelectContent>
        </Select>
      )}

      <Select
        value={filters.account_code || 'all'}
        onValueChange={(value) =>
          onFilterChange('account_code', value === 'all' ? undefined : value)
        }
        disabled={!fundId || isLoadingChartOfAccounts}
      >
        <SelectTrigger className="w-40 h-8">
          <SelectValue placeholder="Account" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Accounts</SelectItem>
          {fundId &&
            !isLoadingChartOfAccounts &&
            chartOfAccounts?.chart_of_accounts &&
            Object.entries(chartOfAccounts.chart_of_accounts).map(
              ([code, name]) => (
                <SelectItem key={code} value={code}>
                  {code} - {name}
                </SelectItem>
              )
            )}
        </SelectContent>
      </Select>

      <Select
        value={filters.reference_type || 'all'}
        onValueChange={(value) =>
          onFilterChange('reference_type', value === 'all' ? undefined : value)
        }
      >
        <SelectTrigger className="w-28 h-8">
          <SelectValue placeholder="Type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          <SelectItem value="drawdown">Drawdown</SelectItem>
          <SelectItem value="payment">Payment</SelectItem>
          <SelectItem value="distribution">Distribution</SelectItem>
          <SelectItem value="adjustment">Adjustment</SelectItem>
          <SelectItem value="investment_purchase">Investment</SelectItem>
          <SelectItem value="valuation_change">Valuation</SelectItem>
          <SelectItem value="asset_sale">Asset Sale</SelectItem>
        </SelectContent>
      </Select>

      <LedgerViewSelect
        value={filters.view || DEFAULT_LEDGER_VIEW}
        onChange={(view) => onFilterChange('view', view)}
      />

      <Input
        type="date"
        placeholder="From"
        value={filters.date_from || ''}
        onChange={(e) =>
          onFilterChange('date_from', e.target.value || undefined)
        }
        className="w-36 h-8"
      />

      <Input
        type="date"
        placeholder="To"
        value={filters.date_to || ''}
        onChange={(e) => onFilterChange('date_to', e.target.value || undefined)}
        className="w-36 h-8"
      />
      <SearchInput
        placeholder="Search reference..."
        value={filters.reference || ''}
        onChange={(value) => onFilterChange('reference', value || undefined)}
      />
      <SearchInput
        placeholder="Search counterparty..."
        value={filters.counterparty || ''}
        onChange={(value) => onFilterChange('counterparty', value || undefined)}
      />

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          className="h-8 px-2 text-xs"
        >
          Clear
        </Button>
      )}
    </div>
  );
}

interface SearchInputProps {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}

function SearchInput({ placeholder, value, onChange }: SearchInputProps) {
  return (
    <div className="relative">
      <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-8 w-[180px] h-8"
      />
    </div>
  );
}
